import {Button, Divider, Menu, MenuItem} from '@mui/material';
import {
  ComponentProps,
  JSXElementConstructor,
  ReactNode,
  useMemo,
  useState,
} from 'react';

import {useGetAssetHumanType} from '../../../hooks/get-commtrac-node-type';
import {
  allMatch,
  BulkSelectionNode,
  supportsBatteryMonitoring,
  supportsBroadcastFrequency,
  supportsTrackMotion,
} from '../../../utils/commtrac-nodes-capabilities';
import CommtracNodeItemsBulkConfirmModal, {
  BulkUpdateField,
} from './CommtracNodeItemsBulkConfirmModal';
import CommtracNodeItemsBulkFrequencyModal from './CommtracNodeItemsBulkFrequencyModal';

type ComponentType = keyof JSX.IntrinsicElements | JSXElementConstructor<any>;

type ActionKey =
  | 'battery-on'
  | 'battery-off'
  | 'motion-on'
  | 'motion-off'
  | 'frequency';

interface Props<T extends ComponentType> {
  ids: BulkSelectionNode[];
  component?: T;
  componentProps?: ComponentProps<T>;
  children?: ReactNode;
  onDone?: () => void;
}

const CommtracNodeItemsUpdateButton = <T extends ComponentType = typeof Button>({
  ids,
  component,
  componentProps,
  children,
  onDone,
}: Props<T>) => {
  const Component = component ?? Button;
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement>();
  const menuOpen = !!menuAnchorEl;
  const [action, setAction] = useState<ActionKey>();

  const closeMenu = () => setMenuAnchorEl(undefined);
  const closeAction = () => setAction(undefined);

  const employeeCount = ids.length;

  const employeeString = useMemo(
    () => `${employeeCount} employee${employeeCount === 1 ? '' : 's'}`,
    [employeeCount]
  );

  const countSuffix = useMemo(
    () => (employeeCount > 0 ? ` (${employeeString})` : ''),
    [employeeCount, employeeString]
  );

  const summary = employeeString;

  const getAssetHumanType = useGetAssetHumanType();

  const supportedIds = useMemo(() => {
    const batteryOk = allMatch(ids, (node) => {
      const subtype = getAssetHumanType({
        wifi_enabled: node.wifi_enabled,
        commtrac_external_id: node.commtrac_external_id,
        mc2_flag: node.mc2_flag,
      });
      return supportsBatteryMonitoring(node, subtype);
    });
    const motionOk = allMatch(ids, supportsTrackMotion);
    const frequencyOk = allMatch(ids, supportsBroadcastFrequency);
    const allIds = ids.map((i) => i.id);
    return {
      battery: batteryOk ? allIds : [],
      motion: motionOk ? allIds : [],
      frequency: frequencyOk ? allIds : [],
    };
  }, [ids, getAssetHumanType]);

  const showBattery = supportedIds.battery.length > 0;
  const showMotion = supportedIds.motion.length > 0;
  const showFrequency = supportedIds.frequency.length > 0;

  const visible = showBattery || showMotion || showFrequency;

  if (!visible) {
    return null;
  }

  const actionConfig: Record<
    Exclude<ActionKey, 'frequency'>,
    {
      ids: number[];
      field: BulkUpdateField;
      value: 0 | 1;
      title: string;
      confirmLabel: string;
      successMessage: string;
    }
  > = {
    'battery-on': {
      ids: supportedIds.battery,
      field: 'cam_monitor_battery',
      value: 1,
      title: 'Enable Battery Monitoring',
      confirmLabel: 'Enable',
      successMessage: 'Battery monitoring enabled',
    },
    'battery-off': {
      ids: supportedIds.battery,
      field: 'cam_monitor_battery',
      value: 0,
      title: 'Disable Battery Monitoring',
      confirmLabel: 'Disable',
      successMessage: 'Battery monitoring disabled',
    },
    'motion-on': {
      ids: supportedIds.motion,
      field: 'has_motion_sensor',
      value: 1,
      title: 'Enable Track Motion',
      confirmLabel: 'Enable',
      successMessage: 'Track motion enabled',
    },
    'motion-off': {
      ids: supportedIds.motion,
      field: 'has_motion_sensor',
      value: 0,
      title: 'Disable Track Motion',
      confirmLabel: 'Disable',
      successMessage: 'Track motion disabled',
    },
  };

  const activeConfirm =
    action && action !== 'frequency' ? actionConfig[action] : null;

  return (
    <>
      <Component
        {...componentProps}
        onClick={(event) => setMenuAnchorEl(event.currentTarget)}
      >
        {children}
      </Component>

      <Menu
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onBackdropClick={closeMenu}
      >
        {showBattery && (
          <MenuItem
            key="battery-on"
            onClick={() => {
              setAction('battery-on');
              closeMenu();
            }}
          >
            {`Enable Battery Monitoring${countSuffix}`}
          </MenuItem>
        )}
        {showBattery && (
          <MenuItem
            key="battery-off"
            onClick={() => {
              setAction('battery-off');
              closeMenu();
            }}
          >
            {`Disable Battery Monitoring${countSuffix}`}
          </MenuItem>
        )}
        {showMotion && (
          <MenuItem
            key="motion-on"
            onClick={() => {
              setAction('motion-on');
              closeMenu();
            }}
          >
            {`Enable Track Motion${countSuffix}`}
          </MenuItem>
        )}
        {showMotion && (
          <MenuItem
            key="motion-off"
            onClick={() => {
              setAction('motion-off');
              closeMenu();
            }}
          >
            {`Disable Track Motion${countSuffix}`}
          </MenuItem>
        )}
        {showFrequency && <Divider key="freq-divider" />}
        {showFrequency && (
          <MenuItem
            key="frequency"
            onClick={() => {
              setAction('frequency');
              closeMenu();
            }}
          >
            {`Set Reporting Frequency${countSuffix}`}
          </MenuItem>
        )}
      </Menu>

      {activeConfirm && (
        <CommtracNodeItemsBulkConfirmModal
          open={!!activeConfirm}
          ids={activeConfirm.ids}
          field={activeConfirm.field}
          value={activeConfirm.value}
          title={activeConfirm.title}
          summary={`Apply to ${summary}?`}
          confirmLabel={activeConfirm.confirmLabel}
          successMessage={activeConfirm.successMessage}
          onClose={closeAction}
          onSubmitted={() => onDone?.()}
        />
      )}

      <CommtracNodeItemsBulkFrequencyModal
        open={action === 'frequency'}
        ids={supportedIds.frequency}
        summary={`Applying to ${summary}`}
        onClose={closeAction}
        onSubmitted={() => onDone?.()}
      />
    </>
  );
};

export default CommtracNodeItemsUpdateButton;
